import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import {
    TimeRequestSelectors,
    DatePickerError,
    OnlineOrOffline,
    DepositConfiguration,
} from "modules/BookingCommon/common/interfaces"
import { OfferedService } from "utils/interfaces"
import { CURRENCY } from "utils/constants"
import { PossibleHoursRecord } from "modules/Booking/common/interfaces/possibleHours"
import { SessionTypePartial, UtcTime } from "modules/Booking/common/interfaces/other"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import { SESSION_TYPE } from "modules/BookingCommon/common/constants"

export interface BookingState {
    deposits: DepositConfiguration[] | undefined
    serviceGroups: ServicesGroup[]
    serviceGroup: ServicesGroup | undefined
    selectedService: OfferedService | undefined
    selectedTime: Date | undefined
    selectedDayOfMonth: Date | undefined
    slotId: string | undefined
    slotDuration: number | undefined
    onlineOrOffline: OnlineOrOffline | undefined
    selectedDiscount: number | undefined
    email: string | undefined
    currency: CURRENCY
    errorMessage: string | undefined
    requestTimesSelectors: TimeRequestSelectors[]
    timeRequestModalActive: boolean
    possibleHours: PossibleHoursRecord | null
    currentWeek: Date | undefined
    sessionType: SessionTypePartial
    timeRequestsUtcTime: UtcTime | null
    selectDeposit: DepositConfiguration | undefined
    hash: string | undefined
    datePickerError: DatePickerError | undefined
    datePickerIsLoaded: boolean
    pagesCount: number
    isLoading: boolean
    isOnline: boolean
    hasPreviousSessions: boolean
    hasPreviousSessionsWithSamePsychologist: boolean
    hasSlots: boolean
    isChooseManuallyService: boolean
    canChangeHistory: boolean
    chooseFlowWithNoAvailable: boolean
    // Флаг возврата из booking на профиль психолога
    returnFromBooking: boolean
    // Флаг, что состояние было восстановлено из BookingStorage
    wasStateRestored: boolean
    // Флаг, что перешли после кнопки запроса времени
    trFlowFromPsyProfile: boolean
}

const initialState: BookingState = {
    deposits: undefined,
    serviceGroups: [],
    serviceGroup: undefined,
    selectedService: undefined,
    selectedTime: undefined,
    selectedDayOfMonth: undefined,
    slotId: undefined,
    slotDuration: undefined,
    onlineOrOffline: undefined,
    selectedDiscount: undefined,
    email: undefined,
    currency: CURRENCY.RUB,
    errorMessage: undefined,
    requestTimesSelectors: [],
    timeRequestModalActive: false,
    possibleHours: null,
    currentWeek: undefined,
    sessionType: SESSION_TYPE.DEFAULT,
    timeRequestsUtcTime: null,
    selectDeposit: undefined,
    hash: undefined,
    datePickerError: undefined,
    datePickerIsLoaded: false,
    pagesCount: 0,
    isLoading: false,
    isOnline: false,
    hasPreviousSessions: false,
    hasPreviousSessionsWithSamePsychologist: false,
    hasSlots: false,
    isChooseManuallyService: false,
    canChangeHistory: true,
    chooseFlowWithNoAvailable: false,
    returnFromBooking: false,
    wasStateRestored: false,
    trFlowFromPsyProfile: false,
}

const bookingSlice = createSlice({
    name: "booking",
    initialState,
    reducers: {
        setDeposits: (state, action: PayloadAction<DepositConfiguration[] | undefined>) => {
            state.deposits = action.payload
        },
        setServiceGroups: (state, action: PayloadAction<ServicesGroup[]>) => {
            state.serviceGroups = action.payload
        },
        setServiceGroup: (state, action: PayloadAction<ServicesGroup | undefined>) => {
            state.serviceGroup = action.payload
        },
        setSelectedService: (state, action: PayloadAction<OfferedService | undefined>) => {
            state.selectedService = action.payload
        },
        setSelectedTime: (state, action: PayloadAction<Date | undefined>) => {
            state.selectedTime = action.payload
        },
        setSelectedDayOfMonth: (state, action: PayloadAction<Date | undefined>) => {
            state.selectedDayOfMonth = action.payload
        },
        setSlotId: (state, action: PayloadAction<string | undefined>) => {
            state.slotId = action.payload
        },
        setSlotDuration: (state, action: PayloadAction<number | undefined>) => {
            state.slotDuration = action.payload
        },
        setOnlineOrOffline: (state, action: PayloadAction<OnlineOrOffline | undefined>) => {
            state.onlineOrOffline = action.payload
            state.isOnline = action.payload === "online"
        },
        setSelectedDiscount: (state, action: PayloadAction<number | undefined>) => {
            state.selectedDiscount = action.payload
        },
        setEmail: (state, action: PayloadAction<string | undefined>) => {
            state.email = action.payload
        },
        setCurrency: (state, action: PayloadAction<CURRENCY>) => {
            state.currency = action.payload
        },
        setErrorMessage: (state, action: PayloadAction<string | undefined>) => {
            state.errorMessage = action.payload
        },
        setTimeRequestsSelectors: (state, action: PayloadAction<TimeRequestSelectors[]>) => {
            state.requestTimesSelectors = action.payload
        },
        setPossibleHours: (state, action: PayloadAction<PossibleHoursRecord | null>) => {
            state.possibleHours = action.payload
        },
        setCurrentWeek: (state, action: PayloadAction<Date | undefined>) => {
            state.currentWeek = action.payload
        },
        setSessionType: (state, action: PayloadAction<SessionTypePartial>) => {
            state.sessionType = action.payload
        },
        setTimeRequestsUtcTime: (state, action: PayloadAction<UtcTime | null>) => {
            state.timeRequestsUtcTime = action.payload
        },
        setSelectDeposit: (state, action: PayloadAction<DepositConfiguration | undefined>) => {
            state.selectDeposit = action.payload
        },
        setHash: (state, action: PayloadAction<string | undefined>) => {
            state.hash = action.payload
        },
        changeSelectedTime: (state, action: PayloadAction<Date>) => {
            state.selectedTime = action.payload
        },
        changeOnlineOrOffline: (state, action: PayloadAction<OnlineOrOffline | undefined>) => {
            state.onlineOrOffline = action.payload
            state.isOnline = action.payload === "online"
        },
        setDatePickerError: (state, action: PayloadAction<DatePickerError | undefined>) => {
            state.datePickerError = action.payload
        },
        toggleDatePickerIsLoaded: (state, action: PayloadAction<boolean>) => {
            state.datePickerIsLoaded = action.payload
        },
        setPagesCount: (state, action: PayloadAction<number>) => {
            state.pagesCount = action.payload
        },
        toggleTimeRequestModalActive: (state, action: PayloadAction<boolean>) => {
            state.timeRequestModalActive = action.payload
        },
        toggleIsLoading: (state, action: PayloadAction<boolean | undefined>) => {
            if (action.payload !== undefined) {
                state.isLoading = action.payload
            } else {
                state.isLoading = !state.isLoading
            }
        },

        setHasPreviousSessions: (state, action: PayloadAction<boolean>) => {
            state.hasPreviousSessions = action.payload
        },
        setHasPreviousSessionsWithSamePsychologist: (state, action: PayloadAction<boolean>) => {
            state.hasPreviousSessionsWithSamePsychologist = action.payload
        },
        setHasSlots: (state, action: PayloadAction<boolean>) => {
            state.hasSlots = action.payload
        },

        setIsChooseManuallyService: (state, action: PayloadAction<boolean>) => {
            state.isChooseManuallyService = action.payload
        },
        setCanChangeHistory: (state, action: PayloadAction<boolean | undefined>) => {
            if (action.payload !== undefined) {
                state.canChangeHistory = action.payload
            } else {
                state.canChangeHistory = !state.canChangeHistory
            }
        },
        setChooseFlowWithNoAvailable: (state, action: PayloadAction<boolean>) => {
            state.chooseFlowWithNoAvailable = action.payload
        },
        setReturnFromBooking: (state, action: PayloadAction<boolean>) => {
            state.returnFromBooking = action.payload
        },
        setWasStateRestored: (state, action: PayloadAction<boolean>) => {
            console.dir("ITS RESTORED")
            console.dir(action.payload)
            state.wasStateRestored = action.payload
        },
        setTrFlowFromPsyProfile: (state, action: PayloadAction<boolean>) => {
            state.trFlowFromPsyProfile = action.payload
        },
        resetBookingState: () => initialState,
        restoreBookingState: (state, action: PayloadAction<Partial<BookingState>>) => {
            // Восстанавливаем только те поля, которые были сохранены
            return {
                ...state,
                ...action.payload,
                isOnline: action.payload.onlineOrOffline === "online",
            }
        },
    },
})

export const bookingSlices = bookingSlice.actions

export const bookingReducer = bookingSlice.reducer
